import { useNavigate } from 'react-router-dom'
import {
  Brain, Shield, Users, Bell, FileText, FlaskConical,
  ChevronRight, Dna, Activity, HeartPulse, Microscope,
  Lock, Zap, BarChart3, GitBranch,
} from 'lucide-react'

// ─── Data ────────────────────────────────────────────────────────────────────

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Log your health data',
    body: 'Connect wearables or enter labs, symptoms, medications, and diet. Each data point is stored as a typed atom in your personal, encrypted Atomspace — owned entirely by you.',
    icon: Activity,
  },
  {
    step: '02',
    title: 'Reasoning engine runs',
    body: 'PLN (Probabilistic Logic Networks) scans your atom history for recurring cause-and-effect patterns. Every finding cites the peer-reviewed evidence behind it.',
    icon: Brain,
  },
  {
    step: '03',
    title: 'Alerts surface early signals',
    body: 'The monitor watches your Atomspace continuously. When a PRECURSOR or TREND pattern forms, you get a plain-language alert with a recommended action — before symptoms escalate.',
    icon: Bell,
  },
  {
    step: '04',
    title: 'Bring your family in',
    body: 'Add parents, grandparents, and siblings (even as proxies for deceased relatives). MOSES mines hereditary patterns across generations and separates genetic risk from modifiable lifestyle factors.',
    icon: Users,
  },
  {
    step: '05',
    title: 'Walk into appointments prepared',
    body: 'Generate a Doctor Brief — a ranked, citable clinical summary with FHIR R4 export — 24 hours before every appointment. Log the outcomes afterward to close the loop.',
    icon: FileText,
  },
]

const FEATURES = [
  {
    icon: Brain,
    title: 'Personal causal models',
    body: 'Not population averages. PLN builds a causal graph from your own longitudinal data with measured lag windows and evidence counts.',
  },
  {
    icon: Dna,
    title: 'Four-generation family graph',
    body: 'Biological and proxy members across four generations. Proxy nodes carry 0.7× confidence so uncertainty from self-reported history is explicit, not hidden.',
  },
  {
    icon: Shield,
    title: 'You own your data',
    body: 'Consent contracts are granular to the atom type — share only sleep data, only biomarkers, or nothing. Every grant and revocation is immutable on-chain.',
  },
  {
    icon: BarChart3,
    title: 'Citable reasoning',
    body: 'Every alert and causal chain links to PubMed IDs or clinical guidelines. If you disagree, submit feedback — the inference engine revises its confidence immediately.',
  },
  {
    icon: GitBranch,
    title: 'MOSES pattern mining',
    body: 'Discovers if-then rules that repeat across generations. Separates what is inherited from what is addressable through lifestyle and intervention.',
  },
  {
    icon: Microscope,
    title: 'Rejuve.Bio research bridge',
    body: 'Opt-in to longevity research. Your data is anonymised with k-anonymity + differential privacy before any export. You earn RJV tokens and get matched findings back.',
  },
  {
    icon: HeartPulse,
    title: 'Caregiver mode',
    body: 'Grant a caregiver monitor-only access — they receive alerts and can log observations without ever seeing your raw biomarker values.',
  },
  {
    icon: Lock,
    title: 'Transparent, not a black box',
    body: 'Every output shows evidence count, confidence, and lag window. The engine learns from your corrections through NegativeObsAtom feedback loops.',
  },
]

const TECH_STACK = [
  { name: 'OpenCog Hyperon', role: 'MeTTa runtime · causal inference rules', color: 'bg-violet-100 text-violet-700' },
  { name: 'Distributed Atomspace', role: 'Personal knowledge graph · MKG storage', color: 'bg-blue-100 text-blue-700' },
  { name: 'PLN', role: 'Probabilistic Logic Networks · temporal reasoning', color: 'bg-sky-100 text-sky-700' },
  { name: 'MOSES', role: 'Multigenerational pattern mining', color: 'bg-indigo-100 text-indigo-700' },
  { name: 'ASI:Chain', role: 'On-chain consent · DID ownership · token ledger', color: 'bg-teal-100 text-teal-700' },
  { name: 'Rejuve.Bio', role: 'Longevity research bridge · RJV rewards', color: 'bg-emerald-100 text-emerald-700' },
  { name: 'HL7 FHIR R4', role: 'Doctor Brief export · EHR interoperability', color: 'bg-amber-100 text-amber-700' },
  { name: 'FastAPI + React', role: 'Async API · real-time WebSocket · Vite frontend', color: 'bg-rose-100 text-rose-700' },
]

const ATOM_TYPES = [
  'Sleep', 'Heart Rate', 'HRV', 'Activity', 'Gait',
  'Biomarker', 'Symptom', 'Medication', 'Adherence',
  'Nutrient', 'Genotype', 'Clinical Obs', 'Context',
]

// ─── Subcomponents ────────────────────────────────────────────────────────────

function NavBar({ onLogin, onStart, onReport }: { onLogin: () => void; onStart: () => void; onReport: () => void }) {
  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-brand-900/95 backdrop-blur border-b border-brand-700/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HeartPulse size={20} className="text-brand-500" />
          <span className="text-white font-bold tracking-tight">FamilyPortal</span>
          <span className="text-xs text-brand-100/50 ml-1 hidden sm:block">v0.2 · Proof of Concept</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onLogin}
            className="text-sm text-brand-100 hover:text-white transition-colors px-3 py-1.5"
          >
            Sign in
          </button>
          <button
            onClick={onReport}
            className="hidden sm:block text-sm text-brand-100 hover:text-white transition-colors px-3 py-1.5"
          >
            Dev Report
          </button>
          <button
            onClick={onStart}
            className="text-sm bg-brand-600 hover:bg-brand-500 text-white px-4 py-1.5 rounded-lg font-medium transition-colors"
          >
            Get started
          </button>
        </div>
      </div>
    </nav>
  )
}

function Hero({ onStart }: { onStart: () => void }) {
  return (
    <section className="pt-32 pb-24 px-6 bg-gradient-to-b from-brand-900 via-brand-900 to-slate-900 text-center">
      <div className="max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-600/20 border border-brand-500/30 text-brand-300 text-xs font-medium mb-6">
          <Zap size={12} />
          Powered by OpenCog Hyperon · SingularityNET open source
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-5">
          Your health, reasoned<br className="hidden sm:block" /> across generations
        </h1>
        <p className="text-lg text-brand-100/70 max-w-2xl mx-auto mb-8 leading-relaxed">
          FamilyPortal is a multigenerational personal health reasoning engine. It builds a causal
          model from your own data, surfaces early signals before they become problems, and connects
          your health story to your family's across four generations — with every finding
          traceable to peer-reviewed evidence.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onStart}
            className="flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-500 text-white rounded-xl font-semibold text-sm transition-colors shadow-lg shadow-brand-900/40"
          >
            Start for free <ChevronRight size={16} />
          </button>
          <a
            href="#how-it-works"
            className="text-sm text-brand-200 hover:text-white transition-colors px-4 py-3"
          >
            See how it works
          </a>
        </div>

        {/* Atom type badges */}
        <div className="mt-14 flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
          {ATOM_TYPES.map(t => (
            <span key={t} className="px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-brand-200 border border-white/10">
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  return (
    <section id="how-it-works" className="py-24 px-6 bg-slate-900">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-3">How it works</h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Five steps from raw health data to actionable, citable insight.
          </p>
        </div>
        <div className="relative">
          {/* connecting line */}
          <div className="absolute left-8 top-8 bottom-8 w-px bg-gradient-to-b from-brand-600 via-brand-500/40 to-transparent hidden md:block" />
          <div className="space-y-10">
            {HOW_IT_WORKS.map(({ step, title, body, icon: Icon }) => (
              <div key={step} className="flex gap-6 md:gap-8">
                <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-brand-600/15 border border-brand-500/30 flex items-center justify-center relative z-10">
                  <Icon size={24} className="text-brand-400" />
                </div>
                <div className="pt-2">
                  <div className="text-xs font-mono text-brand-500 mb-1">{step}</div>
                  <h3 className="text-lg font-semibold text-white mb-1.5">{title}</h3>
                  <p className="text-slate-400 leading-relaxed max-w-xl">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function Features() {
  return (
    <section id="features" className="py-24 px-6 bg-slate-950">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-3">Built differently</h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            FamilyPortal is not a symptom checker or a wellness app. It is a transparent
            reasoning engine that works from your evidence, not population statistics.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-5 hover:bg-white/[0.06] transition-colors">
              <div className="w-10 h-10 rounded-xl bg-brand-600/15 border border-brand-500/20 flex items-center justify-center mb-4">
                <Icon size={18} className="text-brand-400" />
              </div>
              <h3 className="text-sm font-semibold text-white mb-2">{title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function TechStack() {
  return (
    <section className="py-20 px-6 bg-slate-900 border-t border-white/5">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-white mb-3">Open-source foundation</h2>
          <p className="text-slate-400 max-w-lg mx-auto text-sm">
            Built entirely on SingularityNET / ASI Alliance open-source infrastructure.
            No proprietary AI black boxes.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TECH_STACK.map(({ name, role, color }) => (
            <div key={name} className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4">
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold mb-2 ${color}`}>
                {name}
              </span>
              <p className="text-xs text-slate-400">{role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function WhatsMissing() {
  return (
    <section className="py-20 px-6 bg-slate-950 border-t border-white/5">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-white mb-3">What existing tools don't do</h2>
          <p className="text-slate-400 text-sm max-w-lg mx-auto">
            FamilyPortal was built to fill gaps that no existing SingularityNET project addressed.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              gap: 'Temporal PLN for longitudinal streams',
              detail: 'Standard Hyperon PLN operates on static graphs. FamilyPortal adds timestamped lag windows — "ferritin below 35 precedes fatigue 14–21 days later" — computed from your own observation history.',
            },
            {
              gap: 'Health-domain atom ontology',
              detail: 'Hyperon DAS has no health schema. We define 15 typed atom types with provenance, confidence, and versioning semantics aligned with PLN truth values.',
            },
            {
              gap: 'Multi-party consent for cross-Atomspace inference',
              detail: 'MOSES across a family requires querying multiple Atomspaces under different consent scopes. No existing tool provides atom-type-level, on-chain consent with a monitor-only permission tier.',
            },
            {
              gap: 'Proxy members for deceased relatives',
              detail: 'Grandparents are the most informative family nodes — and they can\'t be users. ProxyMemberAtom represents them with 0.7× confidence weighting so their data participates in MOSES without pretending certainty.',
            },
            {
              gap: 'Doctor-ready FHIR export',
              detail: 'Hyperon inference produces knowledge graphs, not clinical documents. FamilyPortal compiles ranked findings into a HL7 FHIR R4 Bundle with PubMed citations that EHR systems can ingest.',
            },
            {
              gap: 'MKG evidence grounding pipeline',
              detail: 'Personal patterns need clinical grounding to be actionable. FamilyPortal builds a Medical Knowledge Graph from WHO, ACC/AHA, ClinVar, and ICD-11 sources and links every causal chain to it.',
            },
          ].map(({ gap, detail }) => (
            <div key={gap} className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-brand-600/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white mb-1">{gap}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function CTA({ onStart }: { onStart: () => void }) {
  return (
    <section className="py-24 px-6 bg-gradient-to-b from-slate-900 to-brand-900">
      <div className="max-w-2xl mx-auto text-center">
        <FlaskConical size={36} className="text-brand-400 mx-auto mb-5" />
        <h2 className="text-3xl font-bold text-white mb-4">
          Ready to reason about your health?
        </h2>
        <p className="text-brand-100/60 mb-8 leading-relaxed">
          FamilyPortal is an open-source proof of concept. Create a free account,
          add a few health data points, and run the reasoning engine to see your
          first causal chain.
        </p>
        <button
          onClick={onStart}
          className="inline-flex items-center gap-2 px-8 py-3.5 bg-brand-600 hover:bg-brand-500 text-white rounded-xl font-semibold transition-colors shadow-xl shadow-brand-900/50"
        >
          Create free account <ChevronRight size={16} />
        </button>
        <p className="mt-4 text-xs text-brand-200/40">
          No credit card · No data sold · Open source
        </p>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="py-8 px-6 bg-brand-900 border-t border-brand-800">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-brand-200/50 text-xs">
          <HeartPulse size={14} className="text-brand-500" />
          FamilyPortal · v0.2 Proof of Concept · 2026
        </div>
        <p className="text-xs text-brand-200/30">
          Built on OpenCog Hyperon · Distributed Atomspace · ASI:Chain
        </p>
      </div>
    </footer>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate()
  const goLogin = () => navigate('/login')
  const goReport = () => navigate('/report')

  return (
    <div className="min-h-screen bg-brand-900 scroll-smooth">
      <NavBar onLogin={goLogin} onStart={goLogin} onReport={goReport} />
      <Hero onStart={goLogin} />
      <HowItWorks />
      <Features />
      <TechStack />
      <WhatsMissing />
      <CTA onStart={goLogin} />
      <Footer />
    </div>
  )
}
